import { useEffect, useRef, useState } from 'react'
import {
  FileSignature,
  Send,
  RefreshCw,
  Search,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  X,
  Download,
  ExternalLink,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import type { AdminCustomer, PaginatedResponse, Proposal, PaginationMeta } from '../../types'
import { formatDate, formatCurrency } from '../../utils/helpers'
import Pagination from '../../components/Pagination'
import ProposalSignatureModal from '../../components/ProposalSignatureModal'

// Proposal enriched with signature status from the portal DB
type AdminProposal = Proposal & {
  signature_status: string // 'none' | 'pending' | 'signed' | 'refused' | 'expired'
  signing_url?: string
  request_id?: string
}

// ERP proposal status (numeric) → label and colour
const ERP_STATUS: Record<number, { label: string; color: string; icon: LucideIcon }> = {
  [-1]: { label: 'Annulé', color: 'bg-gray-100 text-gray-700', icon: XCircle },
  0: { label: 'Brouillon', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  1: { label: 'Validé', color: 'bg-blue-100 text-blue-800', icon: AlertCircle },
  2: { label: 'Signé', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  3: { label: 'Non signé', color: 'bg-red-100 text-red-800', icon: XCircle },
}

const SIG_STATUS: Record<string, { label: string; color: string; icon: LucideIcon }> = {
  none: {
    label: 'adminProposals.sigNone',
    color: 'bg-gray-100 text-gray-600',
    icon: FileSignature,
  },
  pending: {
    label: 'adminProposals.sigPending',
    color: 'bg-yellow-100 text-yellow-800',
    icon: Clock,
  },
  signed: {
    label: 'adminProposals.sigSigned',
    color: 'bg-green-100 text-green-800',
    icon: CheckCircle,
  },
  refused: { label: 'adminProposals.sigRefused', color: 'bg-red-100 text-red-800', icon: XCircle },
  expired: { label: 'adminProposals.sigExpired', color: 'bg-red-100 text-red-800', icon: XCircle },
}

export default function AdminProposals() {
  const { t } = useTranslation()

  // Customer search / selection
  const [customerSearch, setCustomerSearch] = useState('')
  const [customers, setCustomers] = useState<AdminCustomer[]>([])
  const [filteredCustomers, setFilteredCustomers] = useState<AdminCustomer[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<AdminCustomer | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [customersLoaded, setCustomersLoaded] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  // Proposals table state
  const [proposals, setProposals] = useState<AdminProposal[]>([])
  const [pagination, setPagination] = useState<PaginationMeta | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')

  // Proposal for which the signature modal is open
  const [sendingRow, setSendingRow] = useState<AdminProposal | null>(null)

  // PDF download state
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  // Load all admin customers once on mount
  useEffect(() => {
    api
      .getAdminCustomers()
      .then((res) => {
        setCustomers(res)
        setCustomersLoaded(true)
      })
      .catch(() => setCustomersLoaded(true))
  }, [])

  // Filter customers list as user types
  useEffect(() => {
    if (!customerSearch.trim()) {
      setFilteredCustomers(customers.slice(0, 20))
    } else {
      const q = customerSearch.toLowerCase()
      setFilteredCustomers(
        customers
          .filter(
            (c) =>
              c.customer_name?.toLowerCase().includes(q) ||
              c.email?.toLowerCase().includes(q) ||
              c.erp_customer_id?.toLowerCase().includes(q)
          )
          .slice(0, 20)
      )
    }
  }, [customerSearch, customers])

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Load proposals when customer or pagination changes
  useEffect(() => {
    if (selectedCustomer) loadProposals()
  }, [selectedCustomer, page, limit]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadProposals = async () => {
    if (!selectedCustomer) return
    setLoading(true)
    setLoadError('')
    try {
      const res: PaginatedResponse<AdminProposal> = await api.getAdminProposals({
        customer_id: selectedCustomer.id,
        page,
        limit,
      })
      setProposals(res.data)
      setPagination(res.pagination)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setLoadError(apiErr?.response?.data?.error || t('common.error'))
      setProposals([])
    } finally {
      setLoading(false)
    }
  }

  const selectCustomer = (c: AdminCustomer) => {
    setSelectedCustomer(c)
    setCustomerSearch(c.customer_name)
    setShowDropdown(false)
    setPage(1)
    setProposals([])
    setPagination(null)
  }

  const clearCustomer = () => {
    setSelectedCustomer(null)
    setCustomerSearch('')
    setProposals([])
    setPagination(null)
  }

  const openSendModal = (row: AdminProposal) => setSendingRow(row)
  const closeSendModal = () => setSendingRow(null)

  const handleSignatureSuccess = () => {
    // Optimistically update signature_status to pending
    if (sendingRow) {
      setProposals((prev) =>
        prev.map((p) => (p.id === sendingRow.id ? { ...p, signature_status: 'pending' } : p))
      )
    }
    setSendingRow(null)
  }

  const handleDownloadPDF = async (p: AdminProposal) => {
    if (!selectedCustomer) return
    setDownloadingId(p.id)
    try {
      const b64 = await api.downloadAdminProposalPDF(p.id, selectedCustomer.id)
      if (!b64) return
      const blob = new Blob([Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))], {
        type: 'application/pdf',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${p.ref}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      /* silent */
    } finally {
      setDownloadingId(null)
    }
  }

  // Helpers
  const erpStatusInfo = (status: number) =>
    ERP_STATUS[status] ?? {
      label: String(status),
      color: 'bg-gray-100 text-gray-700',
      icon: AlertCircle,
    }
  const sigStatusInfo = (sig: string) => SIG_STATUS[sig] ?? SIG_STATUS['none']

  // A proposal is eligible for sending if it's validated in the ERP and not already signed
  // Use Number() to handle both numeric and string status values from the API
  const canSendInvite = (p: AdminProposal) =>
    Number(p.status) === 1 && !['signed'].includes(p.signature_status)

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileSignature className="h-6 w-6 text-primary-600" />
          {t('adminProposals.title')}
        </h1>
      </div>

      {/* Customer selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('adminProposals.selectCustomer')}
        </label>
        <div className="relative" ref={searchRef}>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value)
                  setShowDropdown(true)
                  if (selectedCustomer) setSelectedCustomer(null)
                }}
                onFocus={() => setShowDropdown(true)}
                placeholder={
                  customersLoaded ? t('adminProposals.searchCustomer') : t('common.loading')
                }
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              />
            </div>
            {selectedCustomer && (
              <button onClick={clearCustomer} className="p-2 text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {showDropdown && filteredCustomers.length > 0 && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filteredCustomers.map((c) => (
                <button
                  key={c.id}
                  onClick={() => selectCustomer(c)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 flex flex-col"
                >
                  <span className="text-sm font-medium text-gray-900">{c.customer_name}</span>
                  <span className="text-xs text-gray-500">{c.email || c.erp_customer_id}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedCustomer && (
          <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span>
              <strong>{selectedCustomer.customer_name}</strong>
              {selectedCustomer.email && <> — {selectedCustomer.email}</>}
            </span>
          </div>
        )}
      </div>

      {/* Proposals table */}
      {selectedCustomer && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
            </div>
          ) : loadError ? (
            <div className="p-6 bg-red-50 text-red-700 text-sm">{loadError}</div>
          ) : proposals.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">
              {t('adminProposals.noProposals')}
            </div>
          ) : (
            <>
              {/* Mobile card layout */}
              <ul className="divide-y divide-gray-100 sm:hidden">
                {proposals.map((p) => {
                  const erpStatus = erpStatusInfo(Number(p.status))
                  const sigStatus = sigStatusInfo(p.signature_status)
                  const ErpIcon = erpStatus.icon
                  const SigIcon = sigStatus.icon
                  return (
                    <li key={p.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 text-sm">{p.ref}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{formatDate(p.date)}</p>
                        </div>
                        <p className="flex-shrink-0 text-sm font-semibold text-primary-700 tabular-nums">
                          {formatCurrency(p.total_excl_tax)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap mb-2">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${erpStatus.color}`}
                        >
                          <ErpIcon className="h-3 w-3" />
                          {erpStatus.label}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sigStatus.color}`}
                        >
                          <SigIcon className="h-3 w-3" />
                          {t(sigStatus.label)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap">
                        <button
                          onClick={() => handleDownloadPDF(p)}
                          disabled={downloadingId === p.id}
                          title={t('adminProposals.downloadPdf')}
                          className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-50"
                        >
                          <Download className="h-3 w-3" />
                          {downloadingId === p.id ? '…' : 'PDF'}
                        </button>
                        {p.signature_status === 'signed' && p.signing_url && (
                          <a
                            href={p.signing_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={t('adminProposals.viewSigned')}
                            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {t('adminProposals.viewSigned')}
                          </a>
                        )}
                        {canSendInvite(p) && (
                          <button
                            onClick={() => openSendModal(p)}
                            className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${p.signature_status === 'pending' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
                          >
                            {p.signature_status === 'pending' ? (
                              <>
                                <RefreshCw className="h-3 w-3" />
                                {t('adminProposals.resend')}
                              </>
                            ) : (
                              <>
                                <Send className="h-3 w-3" />
                                {t('adminProposals.sendInvite')}
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
              {/* Desktop table layout */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      {[
                        'adminProposals.colRef',
                        'adminProposals.colDate',
                        'adminProposals.colValidity',
                        'adminProposals.colTotal',
                        'adminProposals.colStatus',
                        'adminProposals.colSignature',
                        'common.actions',
                      ].map((key) => (
                        <th
                          key={key}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {t(key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {proposals.map((p) => {
                      const erpStatus = erpStatusInfo(Number(p.status))
                      const sigStatus = sigStatusInfo(p.signature_status)
                      const ErpIcon = erpStatus.icon
                      const SigIcon = sigStatus.icon
                      return (
                        <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{p.ref}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatDate(p.date)}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {p.end_validity_date ? formatDate(p.end_validity_date) : '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right tabular-nums">
                            {formatCurrency(p.total_excl_tax)}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${erpStatus.color}`}
                            >
                              <ErpIcon className="h-3 w-3" />
                              {erpStatus.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sigStatus.color}`}
                            >
                              <SigIcon className="h-3 w-3" />
                              {t(sigStatus.label)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 flex-wrap">
                              {/* Download PDF */}
                              <button
                                onClick={() => handleDownloadPDF(p)}
                                disabled={downloadingId === p.id}
                                title={t('adminProposals.downloadPdf')}
                                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-50"
                              >
                                <Download className="h-3 w-3" />
                                {downloadingId === p.id ? '…' : 'PDF'}
                              </button>
                              {/* Link to signed document */}
                              {p.signature_status === 'signed' && p.signing_url && (
                                <a
                                  href={p.signing_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={t('adminProposals.viewSigned')}
                                  className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  {t('adminProposals.viewSigned')}
                                </a>
                              )}
                              {/* Send signature invite */}
                              {canSendInvite(p) && (
                                <button
                                  onClick={() => openSendModal(p)}
                                  className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    p.signature_status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                      : 'bg-primary-600 text-white hover:bg-primary-700'
                                  }`}
                                >
                                  {p.signature_status === 'pending' ? (
                                    <>
                                      <RefreshCw className="h-3 w-3" />
                                      {t('adminProposals.resend')}
                                    </>
                                  ) : (
                                    <>
                                      <Send className="h-3 w-3" />
                                      {t('adminProposals.sendInvite')}
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {pagination && (
                <div className="px-4 pb-4">
                  <Pagination
                    pagination={pagination}
                    onPageChange={setPage}
                    onLimitChange={(n) => {
                      setLimit(n)
                      setPage(1)
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Signature modal — contact selection + send */}
      {sendingRow && selectedCustomer && (
        <ProposalSignatureModal
          proposalId={sendingRow.id}
          proposalRef={sendingRow.ref}
          customerId={selectedCustomer.id}
          onClose={closeSendModal}
          onSuccess={handleSignatureSuccess}
        />
      )}
    </div>
  )
}
